// De SLEUTEL van een skill (eigenaarsbesluit 6 oktober 2026): Front-end en Frontend,
// Vue.js en Vue, Testen en Testing zijn dezelfde skill. Match telde de skilloverlap
// op een letterlijke vergelijking na lower(), dus elke schrijfwijze kostte een treffer.
//
// Een plek voor de regel, twee vormen:
//   skillKey(s)          de canonieke sleutel (kleine letters, zonder accenten en
//                        leestekens, zonder een losse .js-staart, een volle naam
//                        met afkorting op de afkorting, synoniemen samengevouwen)
//   skillVariants(s)     de schrijfwijzen die de opgeslagen skills in de praktijk
//                        hebben, voor de seed van de rangschikking. De SQL blijft dan
//                        een goedkope gelijkheid op lower(sk): de sleutel per
//                        opgeslagen skill uitrekenen maakte de query drie keer zo
//                        traag (gemeten op 590k skillrijen, 6 oktober 2026).
//
// Daarnaast cleanSkill / isSkillJunk: de ene toets die bepaalt of een getypte of
// geimporteerde skill opgeslagen mag worden (13 oktober 2026).
//
// Puur: geen DB, geen IO.

// Synoniemgroepen: elke schrijfwijze in een groep valt op de sleutel van de eerste.
// Bewust kort en alleen voor termen die echt hetzelfde betekenen. Een groep waarvan
// de termen al dezelfde ruwe sleutel hebben ('Pre-sales', 'Presales') staat er voor
// de seed: skillVariants levert dan ook de schrijfwijze met koppelteken.
const SYNONYM_GROUPS = [
  ['Testing', 'Testen'],
  ['SQL Server', 'Microsoft SQL Server', 'MS SQL', 'MSSQL', 'MS SQL Server'],
  ['Power BI', 'Microsoft Power BI', 'PowerBI'],
  ['Excel', 'Microsoft Excel', 'MS Excel'],
  ['Azure', 'Microsoft Azure', 'MS Azure'],
  ['AWS', 'Amazon Web Services'],
  ['GCP', 'Google Cloud Platform', 'Google Cloud'],
  ['Kubernetes', 'K8s'],
  ['PostgreSQL', 'Postgres'],
  ['C#', 'C Sharp', 'CSharp'],
  ['React', 'ReactJS'],
  ['Vue', 'VueJS'],
  ['Node', 'NodeJS'],
  ['Next', 'NextJS'],
  ['Frontend', 'Front-end', 'Front end'],
  ['Backend', 'Back-end', 'Back end'],
  ['Fullstack', 'Full-stack', 'Full stack'],
  ['DevOps', 'Dev Ops', 'Dev-Ops'],
  ['Software Development', 'Softwareontwikkeling'],
  ['Data Analysis', 'Data-analyse', 'Gegevensanalyse', 'Data Analyse'],
  ['Power Query', 'Microsoft Power Query'],
  ['Office 365', 'Microsoft 365', 'M365', 'O365'],
  ['ETL', 'Extract, Transform, Load (ETL)', 'Extract Transform Load'],
  ['CI/CD', 'CICD', 'Continuous Integration'],
  ['Pre-sales', 'Presales'],
  ['Testcoördinator', 'Testcoordinator', 'Test Coordinator'],
  ['Test Coordination', 'Testcoordination'],
  ['React Native', 'ReactNative'],
  ['HTML5', 'HTML 5', 'HTML-5'],
  ['Testautomation', 'Test Automation', 'Testautomatisering'],
  ['Projectmanagement', 'Project Management'],
  ['Data Warehousing', 'Datawarehousing'],
  ['Change Management', 'Changemanagement'],
];

// Skills met een '/' in de naam. Een vrij skillveld splitst op '/' ('AWS/GCP' is twee
// skills); alleen deze blijven heel (lib/market-infer.mjs parseTokens).
export const SLASH_SKILLS = Object.freeze(['CI/CD', 'UI/UX', 'TCP/IP', 'PL/SQL', 'I/O', 'A/B testing', 'OS/2', 'AS/400']);

// Bibliotheken die ook met een .js-staart geschreven worden. Deze krijgen die
// schrijfwijzen in de seed ('Java.js' bestaat niet en blaast de seed alleen op), en
// voor deze valt ook een aaneengeschreven kleine 'js' weg ('nuxtjs').
const JS_LIBS = new Set([
  'react', 'vue', 'node', 'next', 'nuxt', 'express', 'nest', 'ember', 'backbone', 'three', 'd3', 'chart',
  'svelte', 'solid', 'meteor', 'knockout', 'alpine', 'preact', 'deno', 'gulp', 'grunt', 'reactnative',
  'electron', 'babel', 'mocha', 'jasmine', 'polymer', 'gatsby', 'stencil', 'leaflet', 'pug', 'chai', 'pixi',
]);
const isJsLib = (s) => JS_LIBS.has(String(s).replace(/[^a-z0-9]/g, ''));

// AngularJS is een ander framework dan Angular: de staart blijft daar staan.
// Mongojs is een eigen driver, geen MongoDB (toets in lib/skills-key.test.mjs).
const JS_KEEP = new Set(['angular', 'mongo']);

/** Accenten weg: Coördinatie en Coordinatie, Hyvä en Hyva zijn dezelfde schrijfwijze. */
export function foldAccents(s) {
  return String(s == null ? '' : s).normalize('NFD').replace(/[\u0300-\u036f]/g, '');
}

// De .js-staart ('.js', ' js', '-js', 'js') valt weg, nooit tot een lege of
// eenletterige rest ('JS' blijft JS). Met punt of spatie altijd; aaneengeschreven
// alleen bij een bekende bibliotheek of een hoofdletterstaart achter een gewone naam
// ('GatsbyJS', maar 'RxJS' heeft een te korte rest en 'Onderwijs' of 'Kostprijs'
// zijn Nederlandse woorden). Geeft kleine letters terug.
function stripJs(text) {
  const t = String(text || '').trim();
  const lower = t.toLowerCase();
  const sep = lower.match(/^(.*?)(?:\s*[.\s-]\s*)js$/);
  const glued = lower.match(/^(.*[^\s.-])js$/);
  const rest = sep ? sep[1] : glued ? glued[1] : '';
  const restKey = rest.replace(/[^a-z0-9]/g, '');
  if (!restKey || restKey.length < 2) return lower;
  if (JS_KEEP.has(restKey)) return lower;
  if (sep) return rest.trim();
  if (isJsLib(restKey)) return rest;
  const origRest = t.slice(0, t.length - 2);
  if (/JS$/.test(t) && /[a-z]/.test(origRest) && restKey.replace(/[^a-z]/g, '').length >= 3) return rest;
  return lower;
}

// Een omschrijving tussen haakjes achter een skill telt niet mee.
const DESCRIPTORS = new Set(['programming language', 'programmeertaal', 'software', 'tool']);
const ABBR_RE = /^(.+?)\s*\(([A-Z][A-Za-z0-9 &/.+-]{1,11})\)$/;

// Is abbr een afkorting van head? De letters van abbr staan in volgorde in de
// beginletters en hoofdletters van head: 'Amazon Web Services (AWS)', 'PostgreSQL
// (PSQL)', maar niet 'Microsoft Dynamics (CRM)' of 'SAP (FI/CO)'.
function isAbbrOf(abbr, head) {
  const letters = abbr.toLowerCase().replace(/[^a-z]/g, '');
  if (!letters) return false;
  let initials = '';
  for (const word of head.split(/[\s,\-/&]+/).filter(Boolean)) {
    initials += word[0];
    for (const ch of word.slice(1)) if (/[A-Z]/.test(ch)) initials += ch;
  }
  initials = initials.toLowerCase();
  let i = 0;
  for (const ch of initials) if (ch === letters[i]) i++;
  return i === letters.length;
}

// De vorm waarop de sleutel rekent: accenten weg, 'Python (Programming Language)'
// wordt Python en 'Security Information and Event Management (SIEM)' wordt SIEM.
function keyForm(s) {
  const t = foldAccents(s).replace(/\s+/g, ' ').trim();
  const d = t.match(/^(.+?)\s*\(([^()]+)\)$/);
  if (d && DESCRIPTORS.has(d[2].trim().toLowerCase())) return d[1].trim();
  const a = t.match(ABBR_RE);
  if (a && (a[2].match(/[A-Z]/g) || []).length >= 2 && isAbbrOf(a[2], a[1])) return a[2].trim();
  return t;
}

function rawKey(s) {
  return stripJs(keyForm(s)).replace(/[^a-z0-9#+]/g, '');
}

const SYNONYM_OF = new Map();
const GROUP_OF = new Map();
for (const group of SYNONYM_GROUPS) {
  const canon = rawKey(group[0]);
  GROUP_OF.set(canon, group);
  for (const term of group) SYNONYM_OF.set(rawKey(term), canon);
}

/** Voor de toets: elke ruwe sleutel hoort bij hoogstens een synoniemgroep. */
export function synonymGroupClashes() {
  const owner = new Map();
  const clashes = [];
  for (const group of SYNONYM_GROUPS) {
    for (const k of new Set(group.map(rawKey))) {
      if (owner.has(k) && owner.get(k) !== group[0]) clashes.push(`${k}: ${owner.get(k)} en ${group[0]}`);
      owner.set(k, group[0]);
    }
  }
  return clashes;
}

/** De canonieke sleutel van een skill; leeg voor een lege invoer. */
export function skillKey(s) {
  const k = rawKey(s);
  return SYNONYM_OF.get(k) || k;
}

function spellings(term) {
  // Een bibliotheek met .js-staart rekent vanaf zijn kale naam, zodat 'Vue.js' en
  // 'Vue' dezelfde schrijfwijzen geven.
  const base = stripJs(String(term || ''));
  if (!base) return [];
  const out = new Set([base]);
  const parts = base.split(/[\s\-._/]+/).filter(Boolean);
  if (parts.length > 1) for (const sep of ['', ' ', '-', '.', '/']) out.add(parts.join(sep));
  // De .js-staart: 'Vue.js' en 'Vue' en 'VueJS' zijn dezelfde bibliotheek.
  if (isJsLib(base)) for (const tail of ['.js', 'js', ' js']) out.add(`${base}${tail}`);
  return [...out];
}

/**
 * De sleutel waarop een schrijver ontdubbelt: skillKey, en voor een skill zonder
 * sleutel (alleen leestekens of niet-Latijnse tekens) de kleine getrimde vorm, zodat
 * zo'n skill nooit stil wegvalt. Leeg alleen voor een lege invoer.
 */
export function skillDedupeKey(s) {
  const t = String(s == null ? '' : s).trim();
  if (!t) return '';
  return skillKey(t) || t.toLowerCase();
}

// ── Het skillregister (eigenaarsbesluit 15 oktober 2026) ─────────────────────────
// Twee sleutels, elk met EEN rekenplek:
//   name_key      skillDedupeKey(naam), ALLEEN in JS (deze module). skill_names,
//                 skill_kinds en taxonomy_counts (kind 'skill') zijn erop gesleuteld,
//                 dezelfde sleutel waarop elk record ontdubbelt.
//   spelling_key  SKILL_SPELLING_KEY_SQL(x), ALLEEN in Postgres. skill_spellings koppelt
//                 elke schrijfwijze op een record aan zijn name_key. Een SQL-lezer die
//                 recordwaarden aan het register koppelt, joint op deze expressie en
//                 leest daarna sp.name_key. Nooit een JS-toLowerCase() tegen een
//                 SQL-lower() vergelijken: die verschillen op 'İ' (JS geeft i plus een
//                 combinerende punt, Postgres soms niet), op een slot-sigma en op een
//                 harde spatie (JS trim haalt hem weg, btrim niet). Beide kanten van de
//                 vergelijking rekenen dus in dezelfde taal.

/** De SQL-expressie voor de spelling_key van een recordwaarde (zie hierboven). */
export const SKILL_SPELLING_KEY_SQL = (expr) => `lower(btrim(${expr}))`;

/**
 * De schrijfwijzen van een skill in kleine letters, inclusief zijn synoniemen. Elke
 * schrijfwijze van een sleutel geeft dezelfde verzameling, plus altijd de eigen
 * kleine schrijfwijze van de invoer: een opgeslagen 'Coördinatie' of 'Amazon Web
 * Services (AWS)' blijft zo op zijn letterlijke vorm vindbaar.
 */
export function skillVariants(s) {
  const key = skillKey(s);
  if (!key) return [];
  // Een groep levert zijn eigen termen; zonder groep rekent de genormaliseerde vorm
  // (accenten weg, afkorting of kop), nooit de toevallige schrijfwijze.
  const terms = GROUP_OF.get(key) || [keyForm(s)];
  const out = new Set();
  for (const term of terms) for (const v of spellings(foldAccents(term))) out.add(v);
  const own = String(s).replace(/\s+/g, ' ').trim().toLowerCase();
  if (own) out.add(own);
  return [...out];
}

/**
 * De seed van de rangschikking: per opgeslagen schrijfwijze de sleutel. Twee
 * parallelle lijsten, zodat de SQL op lower(sk) blijft vergelijken en per
 * SLEUTEL telt (een kandidaat met 'Frontend' en 'Front-end' telt een keer).
 */
export function expandSeedSkills(skills) {
  const spellingsOut = [];
  const keys = [];
  const seen = new Set();
  for (const s of (Array.isArray(skills) ? skills : [])) {
    const key = skillKey(s);
    if (!key) continue;
    for (const v of skillVariants(s)) {
      if (seen.has(v)) continue;
      seen.add(v);
      spellingsOut.push(v);
      keys.push(key);
    }
  }
  return { spellings: spellingsOut, keys };
}

// ── Rommel en reparatie (13 oktober 2026) ──────────────────────────────────────
// Een plek voor de vraag of een skill opgeslagen mag worden. dedupeSkillsCI en
// canonicalizeSkills (lib/skills-canon.mjs) halen elke skill hier doorheen, dus elke
// schrijver krijgt dezelfde regel.

// Bekende afgeknipte vormen uit de import, met hun bedoelde skill.
const REPAIRS = [
  [/^Continuous Integration and Continuous Delivery \(CI$/i, 'CI/CD'],
  // De tweede helft van dezelfde afgeknipte '(CI/CD)'.
  [/^CD\s*\)$/i, 'CI/CD'],
];
const count = (s, ch) => s.split(ch).length - 1;

/**
 * Een opgeschoonde skill, of '' als er geen skill in zit. Repareert opsommingstekens
 * vooraan, een punt achteraan, een losse sluithaak, haakjes om het geheel en
 * regeleinden. Rommel: geen Latijnse letter of cijfer (na accenten weghalen),
 * ongepaarde haakjes, of langer dan 60 tekens.
 */
export function cleanSkill(s) {
  let t = String(s == null ? '' : s).replace(/\s+/g, ' ').trim();
  for (const [re, to] of REPAIRS) if (re.test(t)) return to;
  for (let prev = ''; prev !== t;) {
    prev = t;
    t = t.replace(/^[\u2022\u25aa\u25e6\u2023\u2043\u00b7]+\s*/, '')
      .replace(/^[-*]+\s+/, '')
      .replace(/\.+$/, '')
      .trim();
    while (t.endsWith(')') && count(t, ')') > count(t, '(')) t = t.slice(0, -1).trim();
    const wrap = t.match(/^\((.*)\)$/);
    if (wrap && count(wrap[1], '(') === count(wrap[1], ')')) t = wrap[1].trim();
  }
  if (!t || t.length > 60) return '';
  if (!/[a-z0-9]/i.test(foldAccents(t))) return '';
  if (count(t, '(') !== count(t, ')')) return '';
  return t;
}

/** Is dit geen skill (ook niet na reparatie)? Zie cleanSkill. */
export function isSkillJunk(s) {
  return cleanSkill(s) === '';
}

// ── Weergavenaam per sleutel (eigenaarsbesluit 13 oktober 2026) ─────────────────
// De sleutel zegt welke schrijfwijzen dezelfde skill zijn; de weergavenaam is de
// schrijfwijze die het meest gebruikt wordt, bij gelijke stand de meeste
// hoofdletters, dan alfabetisch. De volgorde van een synoniemgroep beslist nooit over
// het label. Twee uitzonderingen: een skill uit SLASH_SKILLS draagt zijn
// whitelistvorm (PL/SQL, CI/CD), en een sleutel met een volle naam plus afkorting
// ('Artificial Intelligence (AI)') draagt de afkorting (AI). scripts/taxonomy-merge.mjs
// en canonicalizeSkills (lib/skills-canon.mjs) kiezen allebei hier.

/** De afkorting waarop een 'Volle Naam (AFK)' sleutelt, of ''. */
export function skillAbbreviation(s) {
  const t = foldAccents(s).replace(/\s+/g, ' ').trim();
  const a = t.match(ABBR_RE);
  if (a && (a[2].match(/[A-Z]/g) || []).length >= 2 && isAbbrOf(a[2], a[1])) return a[2].trim();
  return '';
}

const SLASH_BY_KEY = new Map(SLASH_SKILLS.map((s) => [skillDedupeKey(s), s]));
const capsOf = (s) => (s.match(/[A-Z]/g) || []).length;

function mostUsed(counts) {
  let best = null;
  for (const [name, n] of counts) {
    if (!best || n > best.n || (n === best.n && (capsOf(name) > capsOf(best.name)
      || (capsOf(name) === capsOf(best.name) && name < best.name)))) best = { name, n };
  }
  return best ? best.name : '';
}

/**
 * De weergavenaam van een sleutel uit zijn schrijfwijzen met aantallen. Alle namen
 * horen bij dezelfde sleutel (skillDedupeKey); de eerste bepaalt welke.
 * @param {Array<{name: string, n?: number}>} entries
 */
export function pickSkillLabel(entries) {
  const counts = new Map();
  for (const e of Array.isArray(entries) ? entries : []) {
    const name = String(e?.name ?? '').replace(/\s+/g, ' ').trim();
    if (name) counts.set(name, (counts.get(name) || 0) + Math.max(0, Number(e.n) || 0));
  }
  if (!counts.size) return '';
  const key = skillDedupeKey(counts.keys().next().value);
  if (SLASH_BY_KEY.has(key)) return SLASH_BY_KEY.get(key);
  const abbr = new Map();
  for (const [name, n] of counts) {
    const a = skillAbbreviation(name);
    if (a && skillDedupeKey(a) === key) abbr.set(a, (abbr.get(a) || 0) + n);
  }
  if (abbr.size) {
    // Een losse 'AI' telt mee voor de afkorting zelf.
    const lowers = new Map([...abbr.keys()].map((a) => [a.toLowerCase(), a]));
    for (const [name, n] of counts) if (!abbr.has(name) && lowers.has(name.toLowerCase())) abbr.set(name, n);
    return mostUsed(abbr);
  }
  return mostUsed(counts);
}
